import { Component, inject, signal, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimelineTrackerService } from '../../../core/services/dev-tool/timeline-tracker.service';
import { TimelineEvent, TimelineEventType, TimelineRow } from '../../../core/interfaces/dev-tool/timeline.interface';
import { SplitterModule } from '@syncfusion/ej2-angular-layouts';
import { JsonTreeComponent } from '../json-tree/json-tree.component';

@Component({
  selector: 'app-timeline-view',
  standalone: true,
  imports: [CommonModule, FormsModule, SplitterModule, JsonTreeComponent],
  templateUrl: './timeline-view.component.html',
  styleUrls: ['./timeline-view.component.scss']
})
export class TimelineViewComponent {
  private timelineService = inject(TimelineTrackerService);

  // Input: Available height from parent
  contentHeight = input<number>(500); // Default fallback

  // Core data
  events = this.timelineService.getEvents;
  
  // UI State
  zoomLevel = signal(100);
  searchQuery = signal('');
  selectedEvent = signal<TimelineEvent | null>(null);
  activeDataTab = signal<'headers' | 'request' | 'response'>('response');
  
  // Filter state for API timeline
  filters = signal({
    error: true,
    warn: true,
    api: true
  });
  
  // Time range for display (starts at first event, no negative padding)
  timeRange = computed(() => {
    const evts = this.events();
    if (evts.length === 0) return { start: 0, end: 10000 };

    const timestamps = evts.map(e => e.timestamp);
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    // Padding only on the right side (5% of range, minimum 1000ms)
    const range = maxTime - minTime;
    const padding = Math.max(range * 0.05, 1000);

    return { start: minTime, end: maxTime + padding };
  });
  
  // Filtered events based on search and filters - ONLY API CALLS
  filteredEvents = computed(() => {
    const filterState = this.filters();
    const query = this.searchQuery().toLowerCase();

    if (!filterState.api) return [];

    let filtered = this.events().filter(event => event.type === 'api');

    // Apply status filters
    filtered = filtered.filter(event => {
      if (event.status === 'error' && !filterState.error) return false;
      if (event.status === 'warning' && !filterState.warn) return false;
      return true;
    });

    // Apply search filter
    if (query) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(query) ||
        event.description.toLowerCase().includes(query) ||
        (event.data?.url && event.data.url.toLowerCase().includes(query))
      );
    }

    return filtered;
  });
  
  // Convert events to timeline rows with positioning
  timelineRows = computed(() => {
    const filtered = this.filteredEvents();
    const range = this.timeRange();
    const totalDuration = range.end - range.start;
    
    return filtered.map(event => {
      // Calculate position (percentage from start)
      const position = ((event.timestamp - range.start) / totalDuration) * 100;
      
      // Calculate width (duration as percentage, minimum 0.5%)
      const duration = event.duration || 100; // Default 100ms if no duration
      const width = Math.max((duration / totalDuration) * 100, 0.5);
      
      const row: TimelineRow = {
        id: event.id,
        name: this.getEventName(event),
        icon: this.getTypeIcon(event.type),
        event: event,
        position: Math.max(0, Math.min(100, position)),
        width: width
      };
      
      return row;
    });
  });
  
  // Statistics for filter badges (only for API calls)
  errorCount = computed(() => 
    this.events().filter(e => e.type === 'api' && e.status === 'error').length
  );
  
  totalCount = computed(() => 
    this.events().filter(e => e.type === 'api').length
  );
  
  warnCount = computed(() =>
    this.events().filter(e => e.type === 'api' && e.status === 'warning').length
  );

  // Time markers for the scale (count scales with zoom level, labels relative to start)
  // Uses "nice" intervals (500ms, 1s, 2s, 5s, 10s, 30s, 1min) to prevent duplicate labels
  timeMarkers = computed(() => {
    const range = this.timeRange();
    const zoom = this.zoomLevel();
    const markers: { time: number; label: string; position: number }[] = [];
    const totalDuration = range.end - range.start;

    // Target ~5 markers at 100% zoom, scaling with zoom level
    const targetCount = Math.max(3, Math.round(5 * zoom / 100));
    const rawInterval = totalDuration / targetCount;

    // Snap to a "nice" interval to avoid duplicate labels
    const niceInterval = this.getNiceInterval(rawInterval);

    // Generate markers at nice intervals
    let elapsed = 0;
    while (elapsed <= totalDuration) {
      const position = (elapsed / totalDuration) * 100;
      markers.push({
        time: range.start + elapsed,
        label: this.formatElapsed(elapsed),
        position: position
      });
      elapsed += niceInterval;
    }

    return markers;
  });
  
  // Event name extraction - for API calls, show endpoint name
  getEventName(event: TimelineEvent): string {
    // For API calls, extract endpoint name
    if (event.type === 'api') {
      if (event.data?.url) {
        try {
          const url = event.data.url;
          // Handle both full URLs and relative paths
          let path: string;
          
          if (url.startsWith('http://') || url.startsWith('https://')) {
            const urlObj = new URL(url);
            path = urlObj.pathname;
          } else {
            path = url;
          }
          
          const segments = path.split('/').filter(s => s);
          
          if (segments.length > 0) {
            const lastSegment = segments[segments.length - 1];
            // If last segment is a number (ID), use the previous segment
            if (!isNaN(Number(lastSegment)) && segments.length > 1) {
              return segments[segments.length - 2];
            }
            return lastSegment;
          }
        } catch {
          // If URL parsing fails, use title
        }
      }
      
      // Fallback: extract from title (e.g., "GET /api/users" -> "users")
      const match = event.title.match(/\/([^\/\s]+)(?:\?|$|\s)/);
      if (match) {
        return match[1];
      }
    }
    
    // For other events (shouldn't happen now), use title
    return event.title.length > 20 ? event.title.substring(0, 20) + '...' : event.title;
  }
  
  // Get icon for event type
  getTypeIcon(type: TimelineEventType): string {
    const icons: Record<TimelineEventType, string> = {
      console: '💬',
      api: '🌐',
      'state-change': '🔄',
      component: '🧩',
      'user-action': '👆',
      navigation: '🧭',
      error: '❌',
      performance: '⚡'
    };
    return icons[type] || '📍';
  }
  
  // Snap a raw interval to the nearest "nice" value to avoid duplicate/messy labels
  private getNiceInterval(rawInterval: number): number {
    const niceSteps = [
      100, 200, 500,           // ms
      1000, 2000, 5000,        // seconds
      10000, 15000, 30000,     // tens of seconds
      60000, 120000, 300000    // minutes
    ];

    // Find the smallest nice step >= rawInterval
    for (const step of niceSteps) {
      if (step >= rawInterval) return step;
    }

    // For very large ranges, round up to nearest 5 minutes
    return Math.ceil(rawInterval / 300000) * 300000;
  }

  // Format elapsed time for markers (e.g., 0ms, 500ms, 2s, 30s, 2m10s)
  formatElapsed(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m${seconds.toString().padStart(2, '0')}s`;
  }

  // Format time label (MM:SS format)
  formatTimeLabel(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  // Format duration display
  formatDuration(ms: number | undefined): string {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }
  
  // Zoom controls (range: 50% to 500%)
  zoomIn() {
    this.zoomLevel.update(level => {
      const step = level >= 200 ? 50 : 25;
      return Math.min(level + step, 500);
    });
  }

  zoomOut() {
    this.zoomLevel.update(level => {
      const step = level > 200 ? 50 : 25;
      return Math.max(level - step, 50);
    });
  }

  resetZoom() {
    this.zoomLevel.set(100);
  }
  
  // Toggle filter
  toggleFilter(type: 'error' | 'warn' | 'api') {
    this.filters.update(f => ({ ...f, [type]: !f[type] }));
  }
  
  // Select event for details panel
  selectEvent(event: TimelineEvent) {
    this.selectedEvent.set(event);
  }
  
  closeDetails() {
    this.selectedEvent.set(null);
    this.activeDataTab.set('response'); // Reset to default tab
  }

  switchDataTab(tab: 'headers' | 'request' | 'response') {
    this.activeDataTab.set(tab);
  }

  // Helper to get parsed data for display
  getParsedData(data: any): any {
    if (!data) return null;

    // Try to parse if it's a string
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data; // Return as-is if not valid JSON
      }
    }

    return data;
  }
  
  // Clear timeline
  clearTimeline() {
    this.timelineService.clear();
    this.selectedEvent.set(null);
  }
  
  // Export timeline
  exportTimeline() {
    const data = this.timelineService.exportTimeline();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Get status icon
  getStatusIcon(status: string | undefined): string {
    if (status === 'success') return '✓';
    if (status === 'error') return '✗';
    if (status === 'warning') return '⚠';
    return '•';
  }
  
  // Get status color class
  getStatusClass(status: string | undefined): string {
    if (status === 'success') return 'status-success';
    if (status === 'error') return 'status-error';
    if (status === 'warning') return 'status-warning';
    return 'status-default';
  }
}